import { useEffect, useRef, useState } from 'preact/hooks';
import type { WatermarkRect } from '../../render/watermark';

interface Props {
  /** The original photo. */
  before: HTMLCanvasElement;
  /** Show the original across the whole image ("Hold to see original"). */
  holding?: boolean;
  /** Same watermark as the rendered preview, so it covers the whole image wherever the divider is. */
  watermark?: { image: ImageBitmap; rect: WatermarkRect; opacity: number } | null;
}

/**
 * Overlay that reveals the original photo left of a draggable divider.
 * Sits on top of the rendered canvas inside the photo stage.
 */
export function BeforeAfter({ before, holding, watermark }: Props) {
  const [pos, setPos] = useState(50);
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragging = useRef(false);

  useEffect(() => {
    const c = canvasRef.current!;
    c.width = before.width;
    c.height = before.height;
    const ctx = c.getContext('2d')!;
    ctx.drawImage(before, 0, 0);
    if (watermark) {
      const [x, y, w, h] = watermark.rect;
      ctx.globalAlpha = watermark.opacity;
      ctx.drawImage(watermark.image, x * c.width, y * c.height, w * c.width, h * c.height);
      ctx.globalAlpha = 1;
    }
  }, [before, watermark]);

  const fromEvent = (e: PointerEvent) => {
    const r = rootRef.current!.getBoundingClientRect();
    setPos(Math.round(Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100)) * 10) / 10);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    const step = e.shiftKey ? 10 : 2;
    const next =
      e.key === 'ArrowLeft' || e.key === 'ArrowDown' ? pos - step
      : e.key === 'ArrowRight' || e.key === 'ArrowUp' ? pos + step
      : e.key === 'Home' ? 0
      : e.key === 'End' ? 100
      : null;
    if (next === null) return;
    e.preventDefault();
    setPos(Math.min(100, Math.max(0, next)));
  };

  const shown = holding ? 100 : pos;

  return (
    <div
      ref={rootRef}
      class="absolute inset-0 select-none"
      style={{ touchAction: 'pan-y' }}
      onPointerDown={(e) => {
        dragging.current = true;
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        fromEvent(e);
      }}
      onPointerMove={(e) => dragging.current && fromEvent(e)}
      onPointerUp={() => (dragging.current = false)}
      onPointerCancel={() => (dragging.current = false)}
    >
      <canvas
        ref={canvasRef}
        role="img"
        aria-label="Your original floor"
        class="absolute inset-0 h-full w-full"
        style={{ clipPath: `inset(0 ${100 - shown}% 0 0)` }}
      />
      {!holding && (
        <>
          <span class="pointer-events-none absolute left-3 top-3 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">
            Before
          </span>
          <span class="pointer-events-none absolute right-3 top-3 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">
            After
          </span>
          <div class="pointer-events-none absolute inset-y-0 w-0.5 -translate-x-1/2 bg-white shadow-[0_0_0_1px_rgba(0,0,0,.15)]" style={{ left: `${pos}%` }} />
          <div
            role="slider"
            tabIndex={0}
            aria-label="Before and after divider"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pos}
            aria-valuetext={`${Math.round(pos)}% original`}
            onKeyDown={onKeyDown}
            class="absolute top-1/2 grid h-11 w-11 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize place-items-center rounded-full bg-white text-ink shadow-lg"
            style={{ left: `${pos}%` }}
          >
            <svg viewBox="0 0 24 24" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path d="M9 6l-6 6 6 6M15 6l6 6-6 6" />
            </svg>
          </div>
        </>
      )}
    </div>
  );
}
